import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFavorites } from "../../contexts/FavoritesContext";
import { useAuth } from "../../contexts/AuthContext";
import { useAuthModal } from "../../contexts/AuthModalContext";
import { ApiShirt } from "../../types/Shirt";
import type { Auction } from "../../services/auctionService";
import type { ImageUrl } from "../../types/common";

type ShirtCardImageVariant = "optimized" | "thumbnail";

interface ShirtCardProps {
  idx?: number;
  shirt?: ApiShirt;
  auction?: Auction;
  imageVariant?: ShirtCardImageVariant;
  /** Show reserve price badge (seller view only) */
  showReserve?: boolean;
}

type BoostableShirt = ApiShirt & {
  is_boosted?: boolean;
  boosted?: boolean;
  boost?: boolean;
};

/* ---------------- MOCK BOOST SETTINGS ----------------
   Zet later MOCK_BOOST_MODE op false zodra backend gekoppeld is.
----------------------------------------------------- */
const MOCK_BOOST_MODE = true;
const MOCK_BOOST_ALL_SHIRTS = false;

/* Of op basis van id indien je dat fijner vindt */
const MOCK_BOOST_IDS = new Set<string>([]);

function computeTimeLeft(endTime: string): string {
  const diff = new Date(endTime).getTime() - Date.now();
  if (diff <= 0) return "Afgelopen";
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  const mins = Math.floor((diff % 3_600_000) / 60_000);
  if (days > 0) return `${days}d ${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

function useTimeLeft(endTime?: string | null) {
  const [label, setLabel] = useState(() =>
    endTime ? computeTimeLeft(endTime) : "",
  );

  useEffect(() => {
    if (!endTime) { setLabel(""); return; }
    setLabel(computeTimeLeft(endTime));
    const interval = setInterval(() => setLabel(computeTimeLeft(endTime)), 60_000);
    return () => clearInterval(interval);
  }, [endTime]);

  return label;
}

function resolveCardImage(
  image: ImageUrl | undefined,
  preferred: ShirtCardImageVariant,
): string | null {
  if (!image) return null;
  if (preferred === "thumbnail") {
    return image.thumbnail || image.optimized || image.original || null;
  }
  return image.optimized || image.thumbnail || image.original || null;
}

function ShirtCardInner({
  idx = 0,
  shirt,
  auction,
  imageVariant = "optimized",
  showReserve = false,
}: ShirtCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthed } = useAuth();
  const { openAuthModal } = useAuthModal();
  const { isLiked, toggle } = useFavorites();

  const id = shirt ? shirt.id : String(idx);
  const liked = isAuthed ? isLiked(id) : false;

  const [likeCount, setLikeCount] = useState(shirt?.likes_count ?? 0);
  const [showBack, setShowBack] = useState(false);

  const fallbackSrc = "/images/LaLiga_logo.png";

  const sortedImageUrls = useMemo(
    () => [...(shirt?.image_urls ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [shirt],
  );

  const frontSrc = useMemo(() => {
    const image = resolveCardImage(sortedImageUrls[0], imageVariant);
    if (image) return image;
    return "/images/LaLiga_logo.png";
  }, [sortedImageUrls, imageVariant]);

  const hasBack = !!sortedImageUrls[1];
  const backSrc = useMemo(() => {
    const image = resolveCardImage(sortedImageUrls[1], imageVariant);
    if (image) return image;
    return null;
  }, [sortedImageUrls, imageVariant]);

  const [frontOk, setFrontOk] = useState(true);
  const [backOk, setBackOk] = useState(true);

  const visibleSrc =
    showBack && hasBack && backOk && backSrc
      ? backSrc
      : frontOk
        ? frontSrc
        : fallbackSrc;

  const handleClick = () => {
    navigate(isAuction ? `/shirt/auction/${id}` : `/shirt/${id}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isAuthed) {
      openAuthModal();
      return;
    }
    toggle(id, false);
    setLikeCount((c) => (liked ? c - 1 : c + 1));
  };

  const clubName = shirt?.club || "CLUB NAME";
  const playerName = shirt?.player || "";
  const isAuction = shirt?.sales_type === "auction" || !!auction;
  const isMatchworn = shirt?.is_matchworn === true;

  /* echte backend flags later */
  const boostMeta = shirt as BoostableShirt | undefined;
  const backendBoosted =
    boostMeta?.is_boosted === true ||
    boostMeta?.boosted === true ||
    boostMeta?.boost === true;

  /* tijdelijke mock boost */
  const mockBoosted =
    MOCK_BOOST_MODE && (MOCK_BOOST_ALL_SHIRTS || MOCK_BOOST_IDS.has(id));

  const isBoosted = backendBoosted || mockBoosted;

  const currentBid = auction?.current_price;
  const reservePrice = auction?.reserve_price ?? shirt?.auction_reserve_price ?? null;
  const reserveMet =
    reservePrice != null && reservePrice > 0 && currentBid != null && currentBid >= reservePrice;
  const showReserveBadge = showReserve && isAuction && reservePrice != null && reservePrice > 0;

  const auctionDisplayPrice =
    currentBid ?? shirt?.auction_starting_price ?? shirt?.price ?? null;
  const price = isAuction
    ? auctionDisplayPrice != null
      ? `€${auctionDisplayPrice}`
      : "€1"
    : shirt?.price != null
      ? `€${shirt.price}`
      : "€100";
  const season = shirt?.season || "2000 – 2001";
  const size = shirt?.size ? `Maat ${shirt.size}` : "Maat L";
  const timeLeft = useTimeLeft(auction?.end_time ?? shirt?.auction_end_time ?? undefined);

  const cardClasses = [
    "shirt-card",
    isAuction && "shirt-card--auction",
    isMatchworn && "shirt-card--matchworn",
    isBoosted && "shirt-card--boosted",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <article
      className={cardClasses}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setShowBack(true)}
      onMouseLeave={() => setShowBack(false)}
      onFocus={() => setShowBack(true)}
      onBlur={() => setShowBack(false)}
    >
      {(isAuction || isMatchworn) && (
        <div className="shirt-badges">
          {isAuction && (
            <span className="shirt-auction-badge">{t("auction.badge")}</span>
          )}
          {isMatchworn && (
            <span className="shirt-matchworn-badge">Matchissued/-worn</span>
          )}
        </div>
      )}

      {/* Image Zone */}
      <div className="shirt-img-zone">
        {isBoosted && (
          <div
            className={`shirt-boost-corner ${
              isAuction || isMatchworn ? "shirt-boost-badge--with-badges" : ""
            }`}
            aria-label="Boosted listing"
            title="Boosted listing"
          >
            <svg
              className="shirt-boost-icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M13 2L4 13h6l-1 9 11-13h-6l-1-7z" />
            </svg>
          </div>
        )}

        <img
          className="shirt-illustration"
          src={visibleSrc}
          alt={shirt ? `${clubName} shirt` : "Voetbalshirt"}
          loading="lazy"
          onError={() => {
            if (showBack) setBackOk(false);
            else setFrontOk(false);
          }}
        />

        {showReserveBadge && (
          <div className={`shirt-reserve-badge${reserveMet ? " shirt-reserve-badge--met" : ""}`}>
            {t("shirtDetail.reserveBadge", { price: `€${reservePrice}` })}
          </div>
        )}

        <div className="shirt-img-bottom">
          {isAuction && (
            <div className={`shirt-time-overlay${timeLeft === "Afgelopen" ? " shirt-time-overlay--ended" : ""}`}>
              <svg className="shirt-time-overlay__icon" width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                <circle cx="8" cy="9" r="6" stroke="currentColor" strokeWidth="1.5" fill="none" />
                <line x1="8" y1="9" x2="8" y2="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <line x1="8" y1="9" x2="10.5" y2="9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <line x1="6.5" y1="2" x2="9.5" y2="2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              {timeLeft}
            </div>
          )}

          <div className="shirt-wishlist">
            <button
              className={`shirt-heart${liked ? " liked" : ""}`}
              aria-label="Toevoegen aan favorieten"
              onClick={handleLike}
            >
              <img
                src={
                  liked
                    ? "/images/full_heart_icon.svg"
                    : "/images/empty_heart_icon.svg"
                }
                alt=""
              />
            </button>
            <span className="shirt-like-count">{likeCount}</span>
          </div>
        </div>
      </div>

      <div className="shirt-info">
        <div className="shirt-top-row">
          <div className="shirt-text">
            <div className="shirt-club">{clubName}</div>
            <div className="shirt-player">{playerName}</div>
          </div>

          <div className={isAuction ? "shirt-price-block" : ""}>
            {isAuction && (
              <span className="shirt-current-bid-label">current bid</span>
            )}
            <div className="shirt-price">{price}</div>
          </div>
        </div>

        <div className="shirt-pills">
          <span className="shirt-pill shirt-pill-year">{season}</span>
          <span className="shirt-pill shirt-pill-size">{size}</span>
        </div>
      </div>
    </article>
  );
}

const ShirtCard = React.memo(ShirtCardInner);
export default ShirtCard;
